<template>
  <b-modal :id="modalId" centered @shown="shown" @hidden="hide" no-close-on-esc no-close-on-backdrop>
    <template #modal-header>
      <h5 class="modal-title">{{ modalTitle }}</h5>
      <button type="button" aria-label="Close" class="close" @click="$bvModal.hide(modalId)"></button>
    </template>
    <div>
      <Loader v-if="loading"/>
      <div v-show="!loading">
        <Loader v-show="loadingOrder">
          <template #message>{{ $t('pages.checkout.modal_payment_processing_text') }}</template>
        </Loader>

        <section v-if="hasStripeError" class="text-danger">
          <h6>{{ $t('pages.checkout.modal_payment_processing_error_title') }}</h6>
          <p>{{ stripeError.message }}</p>
        </section>

        <div v-if="true" v-show="!loadingOrder">
          <div id="payment-element" class="mb-3"></div>
          <div id="address-element"></div>
        </div>
        <PaymentWithCardInfos class="pb-0" :selected-payment-method="paymentMethod"/>
      </div>
    </div>

    <template #modal-footer>
      <b-button block variant="primary" class="btn--pay" size="md" @click="pay" v-show="!hasStripeError && !loading"
                :disabled="loadingOrder">
        {{ $tc('pages.checkout.action_stripe_pay') }}
        {{ totalCartItems.grand_total | formatCurrency(totalCartItems.currency) }}
      </b-button>
      <b-button block size="md" @click="hide" v-if="hasStripeError">
        {{ $t('pages.checkout.modal_payment_withcard_close') }}
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import Loader from "@/components/Loader"
import PaymentWithCardInfos from "@/components/PaymentWithCardInfos"
import {mapActions, mapGetters, mapMutations} from "vuex";
import {payModes, getAppearanceVariables} from "@/domain/checkout/constants";

export default {
  components: {
    Loader,
    PaymentWithCardInfos
  },

  props: {
    modalId: {
      type: String,
      required: true
    },
    modalTitle: {
      type: String,
      required: false,
      default: 'Pay'
    },
    paymentMethod: {
      type: String,
      required: true
    },
    paymentMethodsStripe: {
      type: Array,
      required: true
    },
    returnUrl: {
      type: String,
      required: false
    }
  },

  data() {
    return {
      loading: false,
      loadingOrder: false,

      elements: null,
      stripe: null,

      addressElement: null,
      paymentElement: null,

      stripeError: {},
    }
  },

  computed: {
    ...mapGetters(['thirdLevelDomain']),
    ...mapGetters('cartDigitalItems', ['totalCartItems']),

    hasStripeError() {
      return Object.keys(this.stripeError).length > 0
    },

    totalCents() {
      return Math.round(this.totalCartItems.grand_total * 100)
    },


    paymentAppearance() {
      return {
        appearance: {
          theme: 'flat',
          ...{variables: getAppearanceVariables(this.thirdLevelDomain)},
          rules: {
            '.Input': {
              fontWeight: '500'
            },
            '.Input:autofill': {},
          }
        }
      }
    },

  },


  methods: {
    ...mapActions('cartDigitalItems', [
      'createOrder',
      'deleteCart',
      'emitFbPixelPurchaseEvent']),

    resetData() {
      this.loading = false
      this.loadingOrder = false
      this.stripeError = {}
      this.addressElement = null
      this.paymentElement = null
    },

    async shown() {
      this.loading = true
      await this.setupStripePayment()
      await this.setupPaymentElement()
      //await this.setupAddressElement()
      await this.$nextTick()
      await this.wait(500)
      this.loading = false
    },

    hide() {
      this.resetData()
      this.$bvModal.hide(this.modalId)
    },


    async setupStripePayment() {
      const orderIntent = await this.createOrder({
        payment_method: payModes.CARD,
      })

      this.stripe = window.Stripe(process.env.VUE_APP_STRIPE_PUBLIC_KEY)
      const paymentAppearance = this.paymentAppearance
      const options = {
        mode: 'payment',
        currency: this.totalCartItems.currency.toLowerCase(),
        amount: this.totalCents,
        paymentMethodTypes: this.paymentMethodsStripe,
        customerSessionClientSecret: orderIntent.customer_session_client_secret,
        ...paymentAppearance,
      }
      this.elements = this.stripe.elements(options)
    },


    async setupPaymentElement() {
      this.paymentElement = this.elements.create("payment", {
        fields: {
          billingDetails: {
            name: 'never',
          }
        }
      })
      this.paymentElement.mount("#payment-element")
    },

    async setupAddressElement() {
      this.addressElement = this.elements.create("address", {
        mode: "billing",
      })
      this.addressElement.mount("#address-element")
    },

    async pay() {
      this.stripeError = {}
      this.loadingOrder = true

      const validationResult = await this.elements.submit()

      if (validationResult.error) {
        // this.displayError(validationResult.error.message)
        this.loadingOrder = false
        return false
      }
      await this.processOrder()
    },

    async processOrder() {
      try {
        const order = await this.createOrder({
          payment_method: payModes.CARD,
        })
        const args = this.getStripeArgs(order)
        const result = await this.stripe.confirmPayment(args)
        if (result.error) {
          this.stripeError = result.error
        } else {
          await this.handleSuccessfulPayment(result, order.order_id)
        }
      } catch (e) {
        console.error('Error processing order', e)
        this.stripeError = e
      } finally {
        this.loadingOrder = false
      }
    },

    getStripeArgs(order) {
      const elements = this.elements
      const returnUrl = this.returnUrl ? {return_url: `${window.location.origin}${this.returnUrl}?order=${order.order_id}`} : {}

      return {
        clientSecret: order.client_secret,
        elements,
        redirect: 'if_required',
        confirmParams: {
          ...returnUrl,
          payment_method_data: {
            billing_details: {
              name: 'never'
            }
          }
        }
      }
    },

    async handleSuccessfulPayment(result, orderId) {
      this.emitFbPixelPurchaseEvent({
        currency: result.paymentIntent.currency,
        amount: result.paymentIntent.amount / 100
      })
      await this.deleteCart()
      await this.$nextTick()
      this.emitPromised('order-success', orderId)
    }
  }
}
</script>
